import React, { useState, useRef, useEffect } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Collapse,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  AddComment as NewChatIcon,
  KeyboardArrowDown as ScrollDownIcon,
  ContentCopy as CopyIcon,
  AttachFile as AttachFileIcon,
  OpenInFull as OpenInFullIcon,
  History as HistoryIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import {
  sendAIChatMessage,
  endAIChatSession,
  createDecisionAgentTurn,
  getDecisionAgentTurn,
  getDecisionAgentThread,
  endDecisionAgentThread,
  uploadDecisionAgentAttachment,
  deleteDecisionAgentAttachment,
  cancelAgentVoiceInput,
} from '../../services/api';
import DictationControl, { appendDictationTranscript } from './DictationControl';
import ThreadReviewControl from './ThreadReviewControl';
import AgentResponseDialog from './AgentResponseDialog';
import AgentHistoryDialog from './AgentHistoryDialog';
import { agentOwner, deliveryKeys, readPending, turnFailureText, CONTACT_LOST_TEXT } from './agentDelivery';
import LiveVoiceControl from './LiveVoiceControl';
import AgentCameraDialog from './AgentCameraDialog';

const TERMINAL_TURN_STATES = new Set(['completed', 'failed']);
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export const compactProgressEvents = (events = []) => {
  const grouped = new Map();
  events.forEach((event) => {
    const key = `${event.status || 'queued'}\u0000${event.label || event.tool || 'Tool step'}`;
    if (!grouped.has(key)) {
      grouped.set(key, { ...event, count: 1 });
    } else {
      grouped.get(key).count += 1;
    }
  });
  return Array.from(grouped.values());
};

const progressSymbol = (status) => {
  if (status === 'completed') return '✓';
  if (status === 'blocked') return '◇';
  if (status === 'failed') return '⚠';
  if (status === 'running') return '●';
  return '○';
};

function AgentProgress({ events = [], terminal = false, tokens = null }) {
  const [expanded, setExpanded] = useState(false);
  if (!events.length && !tokens) return null;

  const compacted = compactProgressEvents(events);
  const latest = events[events.length - 1];
  const tokenTotal = tokens
    ? (tokens.prompt || 0) + (tokens.completion || 0)
      + (tokens.input_tokens || 0) + (tokens.output_tokens || 0)
    : 0;

  if (!terminal) {
    return latest ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {progressSymbol(latest.status)} {latest.label}
        {events.length > 1 ? ` · ${events.length} tool steps so far` : ''}
      </Typography>
    ) : null;
  }

  return (
    <Box sx={{ mt: 0.75 }}>
      <Button
        size="small"
        color="inherit"
        onClick={() => setExpanded((value) => !value)}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        aria-expanded={expanded}
        sx={{ px: 0.5, py: 0, minWidth: 0, color: 'text.secondary', textTransform: 'none' }}
      >
        {expanded ? 'Hide work' : `Show work (${events.length} tool step${events.length === 1 ? '' : 's'})`}
      </Button>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ mt: 0.5, pl: 0.5 }}>
          {compacted.map((event) => (
            <Typography
              key={`${event.id || event.tool}-${event.status}-${event.label}`}
              variant="caption"
              sx={{ display: 'block' }}
              color={event.status === 'failed'
                ? 'warning.main'
                : event.status === 'blocked'
                  ? 'info.main'
                  : 'text.secondary'}
            >
              {progressSymbol(event.status)} {event.label}
              {event.count > 1 ? ` × ${event.count}` : ''}
            </Typography>
          ))}
          {tokenTotal > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {tokenTotal} tokens
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

const createIdempotencyKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `turn-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const turnPhaseLabel = (turn) => {
  if (turn.status === 'queued') return 'Queued for the accounting agent';
  if (turn.phase === 'tools') return 'Working through your financial data';
  if (turn.phase === 'synthesizing') return 'Comparing the validated options';
  if (turn.status === 'failed') return 'The turn stopped before the explanation finished';
  return 'Reasoning through your request';
};

const restoredMessages = (rows = []) => rows.map((message) => ({
  id: message.id,
  turnId: message.turn_id,
  role: message.role,
  content: message.content,
  timestamp: message.created_at,
  tokens: message.tokens_used || null,
  progress: message.tool_runs || [],
  agentStatus: message.agent_status || null,
  agentPhase: message.agent_phase || null,
  phaseLabel: message.agent_status
    ? turnPhaseLabel({ status: message.agent_status, phase: message.agent_phase })
    : null,
  attachments: message.attachments || [],
}));

/**
 * AIChatModal - Modular AI chat interface
 *
 * This component can be used from any page. It receives context
 * that describes what page it's on, what data is available, and
 * whether the user has completed all required inputs.
 *
 * The AI behavior adapts based on context completeness:
 * - Complete context: Substantive financial analysis
 * - Partial context: Helpful form guidance
 */
function AIChatModal({ open, onClose, context, agentic = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const [error, setError] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isDictationBusy, setIsDictationBusy] = useState(false);
  const [dictationResetToken, setDictationResetToken] = useState(0);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [owner, setOwner] = useState(agentOwner);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [threadSelection, setThreadSelection] = useState(0);
  const [threadStatus, setThreadStatus] = useState('active');
  const [voiceState, setVoiceState] = useState({ blocked: false, hasSavedCall: false });
  const voiceNavigationBusy = voiceState.blocked || voiceState.hasSavedCall;
  const [queuedVoiceInputs, setQueuedVoiceInputs] = useState([]);
  const voiceStateRef = useRef(voiceState);
  const voiceHostRef = useRef(null);
  const fileResumeRef = useRef(null);
  const [cameraSurface, setCameraSurface] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const pollGenerationRef = useRef(0);
  const pendingSubmissionRef = useRef(null);
  const submissionBusyRef = useRef(null);
  const restoredSurfaceRef = useRef(null);
  const followLatestRef = useRef(true);
  const keys = deliveryKeys(owner, context?.page);
  const storageKey = keys.thread;
  const attachmentSurfaceRef = useRef(storageKey);
  attachmentSurfaceRef.current = storageKey;

  const savePending = (request) => {
    if (request) window.sessionStorage.setItem(keys.pending, JSON.stringify(request));
    else window.sessionStorage.removeItem(keys.pending);
    pendingSubmissionRef.current = request;
    setPendingSubmission(request);
  };

  const persistThread = (threadId) => {
    try {
      if (threadId) window.sessionStorage.setItem(storageKey, threadId);
      else window.sessionStorage.removeItem(storageKey);
      return true;
    } catch (storageError) {
      console.warn('Conversation continuity storage is unavailable:', storageError);
      return false;
    }
  };

  const applyTurnState = (turn) => {
    const terminal = TERMINAL_TURN_STATES.has(turn.status);
    const content = turn.status === 'completed'
      ? turn.response
      : turn.status === 'failed'
        ? turn.response || turnFailureText(turn)
        : '';
    const assistantMessage = {
      id: `agent-turn-${turn.turn_id}`,
      turnId: turn.turn_id,
      role: 'assistant',
      content,
      timestamp: turn.created_at,
      tokens: terminal ? turn.tokens_used : null,
      progress: turn.tool_runs || [],
      agentStatus: turn.status,
      agentPhase: turn.phase,
      phaseLabel: turnPhaseLabel(turn),
      isError: turn.status === 'failed',
    };
    setMessages((previous) => {
      const index = previous.findIndex((message) => message.turnId === turn.turn_id
        && message.role === 'assistant');
      if (index === -1) {
        // Server history is already sequenced. The optimistic user timestamp is
        // from another clock, so order by the accepted turn, not wall time.
        const userIndex = previous.findIndex((message) => message.turnId === turn.turn_id
          && message.role === 'user');
        const next = [...previous];
        next.splice(userIndex === -1 ? next.length : userIndex + 1, 0, assistantMessage);
        return next;
      }
      const next = [...previous];
      next[index] = { ...next[index], ...assistantMessage };
      return next;
    });
  };

  const reconcileTerminal = (turn) => {
    if (!TERMINAL_TURN_STATES.has(turn.status)) return false;
    if (pendingSubmissionRef.current?.turnId === turn.turn_id
        && !turn.financial_outcome?.reconciliation_required
        && ['committed', 'verified_no_effect'].includes(turn.financial_outcome?.state)) {
      savePending(null);
    }
    setIsLoading(false);
    if (turn.status === 'failed') setError(turnFailureText(turn));
    return true;
  };

  const pollTurn = async (turnId, generation) => {
    let transientFailures = 0;
    while (pollGenerationRef.current === generation) {
      if (agentic && agentOwner() !== owner) {
        setOwner(agentOwner());
        return;
      }
      try {
        const response = await getDecisionAgentTurn(turnId);
        if (pollGenerationRef.current !== generation || agentOwner() !== owner) return;
        transientFailures = 0;
        applyTurnState(response.data);
        if (reconcileTerminal(response.data)) return;
      } catch (pollError) {
        if (pollGenerationRef.current !== generation || agentOwner() !== owner) return;
        transientFailures += 1;
        if (pollError.response?.status === 404 || transientFailures >= 5) {
          setIsLoading(false);
          setError(CONTACT_LOST_TEXT);
          return;
        }
      }
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
    }
  };

  useEffect(() => {
    const refreshOwner = () => setOwner(agentOwner());
    window.addEventListener('storage', refreshOwner);
    window.addEventListener('focus', refreshOwner);
    refreshOwner();
    return () => {
      window.removeEventListener('storage', refreshOwner);
      window.removeEventListener('focus', refreshOwner);
    };
  }, [open]);

  // Polling may change progress without adding a message. Never move a reader
  // who has deliberately scrolled away from the latest content.
  useEffect(() => {
    if (followLatestRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages]);

  // Detect if user has scrolled up
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    followLatestRef.current = isNearBottom;
    setShowScrollButton(!isNearBottom);
  };

  const scrollToBottom = () => {
    followLatestRef.current = true;
    setShowScrollButton(false);
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  };

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset state when modal opens with new context
  useEffect(() => {
    if (open) {
      // Only reset if context page changed
      setError(null);
    }
  }, [open, context?.page]);

  // The server is the source of truth for agentic conversation continuity.
  useEffect(() => {
    if (!agentic || !open) {
      pollGenerationRef.current += 1;
      return undefined;
    }

    const generation = pollGenerationRef.current + 1;
    pollGenerationRef.current = generation;
    const restore = async () => {
      followLatestRef.current = true;
      setShowScrollButton(false);
      setMessages([]);
      setSessionId(null);
      setThreadStatus('restoring');
      setQueuedVoiceInputs([]);
      setError(null);
      if (restoredSurfaceRef.current !== storageKey) {
        setPendingAttachments([]);
        setInputValue('');
        setSelectedResponse(null);
        restoredSurfaceRef.current = storageKey;
      }
      pendingSubmissionRef.current = null;
      setPendingSubmission(null);
      if (!owner || agentOwner() !== owner) {
        setIsLoading(false);
        return;
      }
      let storedThread = null;
      let pending = null;
      let legacyPointer = false;
      const legacyKey = `alloficient.agent.thread.${context?.page || 'general'}`;
      try {
        storedThread = window.sessionStorage.getItem(storageKey);
        pending = readPending(keys.pending, owner);
        // Upgrade old page pointers only after the server verifies this owner.
        if (!storedThread && !pending) {
          storedThread = window.sessionStorage.getItem(legacyKey);
          legacyPointer = Boolean(storedThread);
        }
        pendingSubmissionRef.current = pending;
        setPendingSubmission(pending);
      } catch (storageError) {
        console.warn('Conversation continuity storage is unavailable:', storageError);
      }
      if (pending && !pending.turnId && !pending.payload.thread_id) {
        setThreadStatus('active');
        setMessages([{
          id: `pending-${pending.key}`, role: 'user', content: pending.payload.message,
          timestamp: pending.createdAt, attachments: pending.attachments || [],
        }]);
        await submitPending(pending, generation);
        return;
      }
      storedThread = pending?.threadId || pending?.payload.thread_id || storedThread;
      if (!storedThread) {
        setMessages([]);
        setSessionId(null);
        setThreadStatus('active');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await getDecisionAgentThread(storedThread);
        if (pollGenerationRef.current !== generation || agentOwner() !== owner) return;
        setSessionId(response.data.thread_id);
        setThreadStatus(response.data.status || 'active');
        setQueuedVoiceInputs(response.data.pending_voice_inputs || []);
        if (persistThread(response.data.thread_id) && legacyPointer) {
          window.sessionStorage.removeItem(legacyKey);
        }
        setMessages(restoredMessages(response.data.messages));
        (response.data.failed_turns || []).forEach(applyTurnState);
        if (pending && !pending.turnId) {
          setMessages((current) => [...current, {
            id: `pending-${pending.key}`, role: 'user', content: pending.payload.message,
            timestamp: pending.createdAt, attachments: pending.attachments || [],
          }]);
          await submitPending(pending, generation);
        } else if (response.data.active_turn) {
          applyTurnState(response.data.active_turn);
          await pollTurn(response.data.active_turn.turn_id, generation);
        } else if (pending?.turnId) {
          await pollTurn(pending.turnId, generation);
        } else {
          setIsLoading(false);
        }
      } catch (restoreError) {
        if (pollGenerationRef.current !== generation || agentOwner() !== owner) return;
        if (restoreError.response?.status === 404) {
          persistThread(null);
          setSessionId(null);
          setMessages([]);
          setError('This conversation is unavailable. Start a new chat to continue.');
        } else {
          setError('I could not restore this conversation yet. Please reopen it to retry.');
        }
        setThreadStatus('unavailable');
        setIsLoading(false);
      }
    };
    restore();

    return () => {
      if (pollGenerationRef.current === generation) {
        pollGenerationRef.current += 1;
      }
    };
    // context.page intentionally defines a separate durable conversation surface.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentic, open, storageKey, owner, threadSelection]);

  useEffect(() => { setHistoryOpen(false); }, [owner, open]);

  useEffect(() => {
    if (!agentic || !open || !sessionId || !queuedVoiceInputs.length || (voiceHostRef.current?.run && !voiceHostRef.current.run.disposed)) return undefined;
    let cancelled = false; let timer;
    const readQueue = async () => {
      try {
        const response = await getDecisionAgentThread(sessionId);
        if (cancelled || agentOwner() !== owner) return;
        setMessages(restoredMessages(response.data.messages));
        setQueuedVoiceInputs(response.data.pending_voice_inputs || []);
        (response.data.failed_turns || []).forEach(applyTurnState);
        if (response.data.active_turn) applyTurnState(response.data.active_turn);
        setIsLoading(Boolean(response.data.active_turn));
        if (response.data.active_turn || response.data.pending_voice_inputs?.length) timer = setTimeout(readQueue, 1000);
      } catch (_) { if (!cancelled) timer = setTimeout(readQueue, 3000); }
    };
    readQueue();
    return () => { cancelled = true; clearTimeout(timer); };
    // Only resumes a read-only history observer; never acquires microphone media.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentic, open, owner, sessionId, queuedVoiceInputs.length]);

  const selectConversation = (thread) => {
    if (agentOwner() !== owner || voiceStateRef.current.blocked || voiceStateRef.current.hasSavedCall || pendingSubmissionRef.current || isLoading || isUploadingAttachment || isDictationBusy || inputValue.trim() || pendingAttachments.length) return;
    if (!persistThread(thread.thread_id)) {
      setError('Conversation storage is unavailable. This conversation was not switched.');
      return;
    }
    setHistoryOpen(false);
    setError(null);
    setSelectedResponse(null);
    setThreadSelection((value) => value + 1);
  };

  const submitPending = async (request, generation = pollGenerationRef.current) => {
    if (submissionBusyRef.current === generation || agentOwner() !== request.owner) return;
    submissionBusyRef.current = generation;
    setIsLoading(true);
    setError(null);
    try {
      if (request.turnId) {
        await pollTurn(request.turnId, generation);
        return;
      }
      const response = await createDecisionAgentTurn(request.payload, request.key);
      if (pollGenerationRef.current !== generation || agentOwner() !== request.owner) return;
      const accepted = { ...request, turnId: response.data.turn_id, threadId: response.data.thread_id };
      savePending(accepted);
      setMessages((current) => {
        const alreadyRestored = current.some((message) => (
          message.role === 'user' && message.turnId === accepted.turnId
        ));
        return current.flatMap((message) => {
          if (message.id !== `pending-${request.key}`) return [message];
          return alreadyRestored ? [] : [{ ...message, turnId: accepted.turnId }];
        });
      });
      setPendingAttachments([]);
      setSessionId(response.data.thread_id);
      persistThread(response.data.thread_id);
      applyTurnState(response.data);
      if (reconcileTerminal(response.data)) return;
      await pollTurn(response.data.turn_id, generation);
    } catch (err) {
      if (pollGenerationRef.current !== generation || agentOwner() !== request.owner) return;
      const status = err.response?.status;
      if ([400, 404, 413].includes(status)) {
        savePending(null);
        setInputValue(request.payload.message);
        setPendingAttachments(request.attachments || []);
        setMessages((current) => current.filter((message) => message.id !== `pending-${request.key}`));
        setError(err.response?.data?.error || 'The request was rejected before acceptance.');
      } else {
        setError(err.response?.data?.error || 'The request acknowledgment was lost. Reconnect to reconcile this same request.');
      }
    } finally {
      if (submissionBusyRef.current === generation) submissionBusyRef.current = null;
      if (pollGenerationRef.current === generation) setIsLoading(false);
    }
  };

  const handleSend = async () => {
    const message = inputValue.trim();
    if (!message || isLoading || isDictationBusy || isUploadingAttachment || (agentic && (voiceStateRef.current.blocked || queuedVoiceInputs.length || pendingSubmissionRef.current || threadStatus !== 'active'))) return;

    const attachments = [...pendingAttachments];

    if (agentic) {
      const requestOwner = agentOwner();
      if (!requestOwner || requestOwner !== owner) {
        setOwner(requestOwner);
        setError('Sign in to this profile before sending a request.');
        return;
      }
      // Freeze and persist before any network call, including a first null thread.
      const request = JSON.parse(JSON.stringify({
        owner, key: createIdempotencyKey(), createdAt: new Date().toISOString(), attachments,
        payload: { message, thread_id: sessionId, context: buildContextPayload(context),
          attachment_ids: attachments.map((attachment) => attachment.attachment_id) },
      }));
      try {
        savePending(request);
      } catch (_) {
        setError('This request could not be saved for safe retry. Enable conversation storage before sending.');
        return;
      }
      setInputValue('');
      setMessages((current) => [...current, {
        id: `pending-${request.key}`, role: 'user', content: message,
        timestamp: request.createdAt, attachments,
      }]);
      await submitPending(request);
      return;
    }

    setInputValue('');
    setError(null);

    // Add user message to UI immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      attachments,
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content,
      }));
      const response = await sendAIChatMessage({
        message,
        session_id: sessionId,
        context: buildContextPayload(context),
        conversation_history: conversationHistory,
      });
      if (response.data.session_id) setSessionId(response.data.session_id);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        tokens: response.data.tokens_used,
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');

      // Add error message to UI
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) setSelectedResponse(null);
  }, [open]);

  const handleClose = () => {
    setCameraSurface(null);
    voiceHostRef.current?.dispose();
    setSelectedResponse(null);
    onClose();
  };

  const openFormattedResponse = (message) => {
    if (message.role === 'assistant' && message.content) setSelectedResponse(message);
  };

  const handleResponseKeyDown = (event, message) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFormattedResponse(message);
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    try { await uploadAttachments(files); }
    finally { fileResumeRef.current?.(); fileResumeRef.current = null; }
  };

  const uploadAttachments = async (files) => {
    if (!agentic || !files.length || pendingSubmissionRef.current || isUploadingAttachment) return false;
    const uploadOwner = agentOwner();
    const uploadSurface = storageKey; const uploadGeneration = pollGenerationRef.current;
    const sameSurface = () => agentOwner() === uploadOwner && attachmentSurfaceRef.current === uploadSurface && pollGenerationRef.current === uploadGeneration;
    if (!uploadOwner || uploadOwner !== owner) return false;
    if (pendingAttachments.length + files.length > 5) {
      setError('A message may include at most five attachments.');
      return false;
    }
    if (files.some((file) => file.size > MAX_ATTACHMENT_BYTES)) {
      setError('Each attachment must be 10 MiB or smaller.');
      return false;
    }
    setError(null);
    setIsUploadingAttachment(true);
    const uploaded = [];
    try {
      for (const file of files) {
        const response = await uploadDecisionAgentAttachment(file, uploadOwner);
        if (!sameSurface()) {
          await Promise.allSettled([...uploaded, response.data].map((attachment) => deleteDecisionAgentAttachment(attachment.attachment_id, uploadOwner)));
          if (agentOwner() !== uploadOwner) setOwner(agentOwner());
          return false;
        }
        uploaded.push(response.data);
      }
      setPendingAttachments((current) => [...current, ...uploaded]);
      return true;
    } catch (uploadError) {
      await Promise.allSettled(uploaded.map(
        (attachment) => deleteDecisionAgentAttachment(attachment.attachment_id, uploadOwner)
      ));
      if (sameSurface()) setError(uploadError.response?.data?.error || (
        uploadError.response?.status === 413
          ? 'The upload was rejected by the server size limit. Each attachment must be 10 MiB or smaller.'
          : 'The attachment could not be uploaded. Please try again.'
      ));
      return false;
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const removePendingAttachment = async (attachmentId) => {
    if (pendingSubmissionRef.current || agentOwner() !== owner) return;
    try {
      await deleteDecisionAgentAttachment(attachmentId, owner);
      setPendingAttachments((current) => current.filter(
        (attachment) => attachment.attachment_id !== attachmentId
      ));
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || 'The pending attachment could not be removed.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = async () => {
    if (agentic && (pendingSubmissionRef.current || voiceStateRef.current.blocked || voiceStateRef.current.hasSavedCall || queuedVoiceInputs.length)) return;
    await Promise.allSettled(pendingAttachments.map(
      (attachment) => deleteDecisionAgentAttachment(attachment.attachment_id, owner)
    ));
    // End current session if exists
    if (sessionId) {
      try {
        if (agentic) {
          await endDecisionAgentThread(sessionId);
        } else {
          await endAIChatSession(sessionId);
        }
      } catch (err) {
        console.warn('Failed to end session:', err);
        // Continue anyway - not critical
      }
    }

    // Clear local state
    setMessages([]);
    setSessionId(null);
    setThreadStatus('active');
    followLatestRef.current = true;
    setShowScrollButton(false);
    if (agentic) persistThread(null);
    pollGenerationRef.current += 1;
    setError(null);
    setInputValue('');
    setDictationResetToken((value) => value + 1);
    setPendingAttachments([]);
  };

  const handleCopyChat = async () => {
    if (messages.length === 0) return;

    const chatText = messages
      .map((msg) => `${msg.role === 'user' ? 'You' : 'AI'}: ${msg.content}`)
      .join('\n\n');

    try {
      await navigator.clipboard.writeText(chatText);
      // Could add a snackbar notification here
    } catch (err) {
      console.error('Failed to copy chat:', err);
    }
  };

  const buildContextPayload = (ctx) => {
    if (!ctx) {
      return {
        page: 'general',
        purpose: 'General financial assistance',
        is_complete: false,
        current_values: {},
      };
    }

    return {
      page: ctx.page || 'unknown',
      purpose: ctx.purpose || 'General assistance',
      workflow: ctx.workflow || null,
      schema_fields: ctx.schema_fields || ctx.schemaFields || null,
      current_values: ctx.current_values || ctx.currentValues || {},
      computed_results: ctx.computed_results || ctx.computedResults || null,
      is_complete: ctx.is_complete ?? ctx.isComplete ?? false,
      missing_required: ctx.missing_required || ctx.missingRequired || [],
      hints: ctx.hints || null,
    };
  };

  const isComplete = context?.is_complete ?? context?.isComplete ?? false;

  const getContextStatusChip = () => {
    if (agentic) {
      return <Chip label="Tool-enabled" size="small" color="success" />;
    }
    if (!context) {
      return <Chip label="General Help" size="small" color="default" />;
    }
    if (isComplete) {
      return <Chip label="Full Context" size="small" color="success" />;
    }
    return <Chip label="Partial Context" size="small" color="warning" />;
  };

  const getWelcomeMessage = () => {
    if (agentic) {
      return "Tell me what you're trying to decide. I can inspect your current financial network, recall your priorities, and validate multiple options before I recommend one.";
    }
    if (!context) {
      return "Hi! I'm your AlloFicient assistant. How can I help you today?";
    }
    if (isComplete) {
      return `I have full context about your ${context.page?.replace(/_/g, ' ')}. Ask me anything about your scenario!`;
    }
    return `I can help you with the ${context.page?.replace(/_/g, ' ')}. Complete the form for detailed analysis, or ask me questions about how to fill it out.`;
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: 600,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          pb: 1,
        }}
      >
        {/* Keep Close beside the title; let conversation actions wrap below on mobile. */}
        <Box sx={{ display: 'grid', alignItems: 'center', columnGap: 1, rowGap: 0.5,
          gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1fr) auto auto' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, gridColumn: 1, gridRow: 1 }}>
            <BotIcon color="primary" sx={{ flexShrink: 0 }} />
            <Typography variant="h6" sx={{ minWidth: 0, overflowWrap: 'break-word' }}>{agentic ? 'Accounting Agent' : 'AI Assistant'}</Typography>
            {/* Desktop: Show chip inline */}
            {!isMobile && getContextStatusChip()}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minWidth: 0,
            gridColumn: { xs: '1 / -1', sm: 2 }, gridRow: { xs: 2, sm: 1 },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            {agentic && sessionId && (messages.length > 0 || voiceState.hasSavedCall) && (
              <ThreadReviewControl threadId={sessionId} disabled={isLoading || voiceNavigationBusy || queuedVoiceInputs.length > 0 || Boolean(pendingSubmission)} />
            )}
            {agentic && (
              <Tooltip title={voiceNavigationBusy ? 'End or reconcile voice before switching conversations' : inputValue.trim() || pendingAttachments.length ? 'Send or clear your draft before switching conversations' : 'Conversation history'}>
                <span><IconButton size="small" aria-label="Conversation history" disabled={isLoading || voiceNavigationBusy || queuedVoiceInputs.length > 0 || Boolean(pendingSubmission) || isUploadingAttachment || isDictationBusy || Boolean(inputValue.trim()) || pendingAttachments.length > 0} onClick={() => setHistoryOpen(true)}><HistoryIcon /></IconButton></span>
              </Tooltip>
            )}
            {messages.length > 0 && (
              <>
                <Tooltip title="Copy chat">
                  <IconButton onClick={handleCopyChat} size="small" aria-label="Copy chat">
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Start new chat">
                  <span>
                    <IconButton onClick={handleNewChat} size="small" aria-label="Start new chat" disabled={isLoading || voiceNavigationBusy || queuedVoiceInputs.length > 0 || Boolean(pendingSubmission)}>
                      <NewChatIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label="Close accounting agent"
            sx={{ gridColumn: { xs: 2, sm: 3 }, gridRow: 1, alignSelf: 'start', minWidth: 40, minHeight: 40 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Mobile: Show chip on its own row, centered */}
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            {getContextStatusChip()}
          </Box>
        )}
      </DialogTitle>

      <Divider />

      {agentic && threadStatus === 'ended' && (
        <Alert severity="info" sx={{ mx: 2, mt: 1 }}>
          This conversation has ended and is read only. Start a new chat to continue.
        </Alert>
      )}
      {agentic && threadStatus === 'unavailable' && (
        <Alert severity="warning" sx={{ mx: 2, mt: 1 }} action={<Button aria-label="Start new chat" onClick={handleNewChat}>Start new chat</Button>}>
          Sending is paused until this conversation can be restored. Reopen it to retry, or start a new chat.
        </Alert>
      )}

      {/* Context Info (Collapsible) */}
      {context && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setShowContext(!showContext)}
          >
            <InfoIcon fontSize="small" sx={{ mr: 1 }} color="action" />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {context.purpose || 'Current page context'}
            </Typography>
            {showContext ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={showContext}>
            <Box sx={{ mt: 1, pl: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Page: {context.page}<br />
                Status: {context.isComplete ? 'Complete' : 'Incomplete'}
                {context.missingRequired?.length > 0 && (
                  <><br />Missing: {context.missingRequired.join(', ')}</>
                )}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Messages Area */}
      <DialogContent
        ref={messagesContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          py: 2,
          position: 'relative',
        }}
      >
        {/* Welcome message if no messages yet */}
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <BotIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {getWelcomeMessage()}
            </Typography>
          </Box>
        )}

        {/* Message list */}
        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                maxWidth: '80%',
                bgcolor: msg.role === 'user' ? 'primary.main' : msg.isError ? 'error.light' : 'grey.100',
                color: msg.role === 'user' ? 'white' : 'text.primary',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {msg.role === 'assistant' && (
                  <BotIcon fontSize="small" sx={{ mt: 0.5 }} />
                )}
                <Box>
                  {msg.agentStatus && !TERMINAL_TURN_STATES.has(msg.agentStatus) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: msg.progress?.length ? 0.75 : 0 }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" color="text.secondary">
                        {msg.phaseLabel}
                      </Typography>
                    </Box>
                  )}
                  {msg.content && (
                    <Box
                      role={msg.role === 'assistant' ? 'button' : undefined}
                      tabIndex={msg.role === 'assistant' ? 0 : undefined}
                      aria-label={msg.role === 'assistant' ? 'Open formatted assistant response' : undefined}
                      onClick={() => openFormattedResponse(msg)}
                      onKeyDown={(event) => handleResponseKeyDown(event, msg)}
                      sx={{
                        cursor: msg.role === 'assistant' ? 'pointer' : 'default',
                        borderRadius: 1,
                        '&:focus-visible': msg.role === 'assistant' ? {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: 3,
                        } : undefined,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {msg.content}
                      </Typography>
                      {msg.role === 'assistant' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, color: 'primary.main' }}>
                          <OpenInFullIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">View formatted response</Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {msg.attachments?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                      {msg.attachments.map((attachment) => (
                        <Chip
                          key={attachment.attachment_id}
                          size="small"
                          icon={<AttachFileIcon />}
                          label={attachment.filename}
                          sx={msg.role === 'user' ? { bgcolor: 'rgba(255,255,255,0.2)', color: 'white' } : {}}
                        />
                      ))}
                    </Box>
                  )}
                  <AgentProgress
                    events={msg.progress || []}
                    terminal={TERMINAL_TURN_STATES.has(msg.agentStatus)}
                    tokens={msg.tokens}
                  />
                </Box>
                {msg.role === 'user' && (
                  <PersonIcon fontSize="small" sx={{ mt: 0.5 }} />
                )}
              </Box>
            </Paper>
          </Box>
        ))}

        {/* Loading indicator */}
        {isLoading && !agentic && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                bgcolor: 'grey.100',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {agentic ? 'Working with your current financial data…' : 'Thinking...'}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Error message */}
        {error && (
          <Typography variant="caption" color="error" sx={{ textAlign: 'center' }}>
            {error}
          </Typography>
        )}
        {agentic && pendingSubmission && !isLoading && (
          <Button onClick={() => submitPending(pendingSubmission)}>
            Reconnect to pending request
          </Button>
        )}

        <div ref={messagesEndRef} />
      </DialogContent>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }}
        >
          <Tooltip title="Scroll to latest">
            <IconButton
              size="small"
              onClick={scrollToBottom}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <ScrollDownIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Divider />

      {/* Input Area */}
      {agentic && <LiveVoiceControl
        active={open} owner={owner} page={context?.page} threadId={sessionId} controlRef={voiceHostRef}
        disabled={isLoading || isDictationBusy || isUploadingAttachment || Boolean(pendingSubmission) || Boolean(inputValue.trim()) || threadStatus !== 'active' || queuedVoiceInputs.length > 0}
        getContext={() => buildContextPayload(context)} getAttachments={() => pendingAttachments}
        onThread={(id) => {
          if (agentOwner() !== owner || !persistThread(id)) return false;
          setSessionId(id); return true;
        }}
        onSnapshot={(snapshot) => {
          setSessionId(snapshot.thread_id); setThreadStatus(snapshot.status || 'active');
          setMessages(restoredMessages(snapshot.messages));
          (snapshot.failed_turns || []).forEach(applyTurnState);
          setQueuedVoiceInputs(snapshot.pending_voice_inputs || []);
          if (snapshot.active_turn) applyTurnState(snapshot.active_turn);
          setIsLoading(Boolean(snapshot.active_turn));
        }}
        onAccepted={(_, ids) => setPendingAttachments((current) => current.filter((item) => !ids.includes(item.attachment_id)))}
        onState={(state) => { voiceStateRef.current = state; setVoiceState(state); }}
      />}
      {agentic && queuedVoiceInputs.length > 0 && <Box sx={{ px: 2, pt: 1 }}>
        {queuedVoiceInputs.map((item) => <Alert severity={item.status === 'needs_attention' ? 'warning' : 'info'} key={item.input_id}>
          {item.status === 'needs_attention' ? 'Voice request needs attention' : 'Queued voice request'}: {item.message}
          <Button size="small" onClick={async () => {
            if (agentOwner() !== owner) return;
            try {
              await cancelAgentVoiceInput(sessionId, item.input_id, owner);
              const reply = await getDecisionAgentThread(sessionId);
              if (agentOwner() === owner) setQueuedVoiceInputs(reply.data.pending_voice_inputs || []);
            } catch (failure) { if (agentOwner() === owner) setError(failure.response?.data?.error || 'This request could not be cancelled. Refresh to see whether it was already accepted.'); }
          }}>Cancel queued request</Button>
        </Alert>)}
      </Box>}
      {agentic && pendingAttachments.length > 0 && (
        <Box sx={{ px: 2, pt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {pendingAttachments.map((attachment) => (
            <Chip
              key={attachment.attachment_id}
              size="small"
              icon={<AttachFileIcon />}
              label={attachment.filename}
              onDelete={() => removePendingAttachment(attachment.attachment_id)}
              disabled={isLoading || Boolean(pendingSubmission)}
            />
          ))}
        </Box>
      )}
      <Box sx={{ p: 2, pb: 3, display: 'flex', gap: 1, position: 'relative' }}>
        {agentic && (
          <>
            <input
              ref={attachmentInputRef}
              type="file"
              multiple
              hidden
              accept=".csv,.docx,.jpeg,.jpg,.json,.md,.pdf,.png,.txt,.xlsx"
              onChange={handleAttachmentChange}
              onCancel={() => { fileResumeRef.current?.(); fileResumeRef.current = null; }}
            />
            <Tooltip title="Attach an image or document">
              <span>
                <IconButton
                  onClick={() => { fileResumeRef.current = voiceHostRef.current?.pauseForAttachment(); attachmentInputRef.current?.click(); }}
                  disabled={(isLoading && !voiceState.captureActive) || Boolean(pendingSubmission) || isUploadingAttachment || pendingAttachments.length >= 5 || threadStatus !== 'active'}
                  aria-label="Attach an image or document"
                >
                  {isUploadingAttachment ? <CircularProgress size={20} /> : <AttachFileIcon />}
                </IconButton>
              </span>
            </Tooltip>
            {voiceState.captureActive && <Tooltip title="Take a photo without leaving the call"><span>
              <IconButton aria-label="Take a photo" disabled={isUploadingAttachment || pendingAttachments.length >= 5}
                onClick={() => setCameraSurface({ owner, page: context?.page })}><PhotoCameraIcon /></IconButton>
            </span></Tooltip>}
          </>
        )}
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={isComplete
            ? "Ask about your scenario..."
            : "Ask a question or get help..."
          }
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          inputRef={inputRef}
          disabled={isLoading || Boolean(pendingSubmission) || (agentic && (voiceState.blocked || queuedVoiceInputs.length > 0 || threadStatus !== 'active'))}
          multiline
          maxRows={3}
        />
        <DictationControl
          active={open}
          disabled={isLoading || Boolean(pendingSubmission) || (agentic && (voiceState.blocked || queuedVoiceInputs.length > 0 || threadStatus !== 'active'))}
          onBusyChange={setIsDictationBusy}
          onTranscript={(transcript) => {
            setInputValue((draft) => appendDictationTranscript(draft, transcript));
            window.setTimeout(() => inputRef.current?.focus(), 0);
          }}
          resetToken={dictationResetToken}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          aria-label="Send message"
          disabled={!inputValue.trim() || isLoading || Boolean(pendingSubmission) || isDictationBusy || isUploadingAttachment || (agentic && (voiceState.blocked || queuedVoiceInputs.length > 0 || threadStatus !== 'active'))}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&:disabled': { bgcolor: 'grey.300' },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Dialog>
    {agentic && <AgentHistoryDialog
      open={historyOpen && open}
      owner={owner}
      onClose={() => setHistoryOpen(false)}
      onSelect={selectConversation}
    />}
    <AgentResponseDialog
      open={Boolean(selectedResponse)}
      content={selectedResponse?.content || ''}
      onClose={() => setSelectedResponse(null)}
    />
    <AgentCameraDialog open={open && cameraSurface?.owner === owner && cameraSurface?.page === context?.page && Boolean(cameraSurface)}
      owner={owner} currentOwner={agentOwner} onClose={() => setCameraSurface(null)}
      onOpen={() => voiceHostRef.current?.pauseForAttachment()}
      onCapture={(file) => uploadAttachments([file])} />
    </>
  );
}

export default AIChatModal;
